import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import Span from '../../../../common/text/Span';
import { BLACK, GRAY, LIGHT_GRAY, ORANGE } from '../../../../../styles/constants/color';
import { fontFrame } from '../../../../../styles/mixin/font';
import OtherBankTypeContents from '../contents/OtherBankTypeContents';
import OtherBankPeriodContents from '../contents/OtherBankPeriodContents';
import OtherBankAdvanceReceivedRateContents from '../contents/OtherBankAdvanceReceivedRateContents';
import OtherBankInstallmentPrincipalContents from '../contents/OtherBankInstallmentPrincipalContents';
import OtherBankPaymentTypeContents from '../contents/OtherBankPaymentTypeContents';
import useCalculatorForOtherBankCondition from '../../../../../hooks/estimate/common/otherBank/useCalculatorForOtherBankCondition';
import { useRecoilValue } from 'recoil';
import { otherBankInfoState } from '../../../../../data/estimate/common/otherBank/info/selector';
import useDevice from '../../../../../hooks/common/useDevice';

const Table = styled.table`
  width: 228px;
  height: 551px;
  border: 1px solid ${GRAY};

  & tbody th {
    border-right: 1px solid ${GRAY};
    border-bottom: 1px solid ${GRAY};
  }

  & tbody td {
    height: 40px;
    padding: 0 8px;
  }

  & tbody td:not(.not-border-bottom) {
    border-bottom: 1px solid ${GRAY};
  }

  & > tbody > tr:last-of-type td {
    background-color: #f2f3f5;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Header = styled.th`
  border-top: 1px solid ${BLACK};
  background-color: ${LIGHT_GRAY};
  height: 50px;
  border-bottom: 1px solid ${GRAY};
`;

const Th = styled.th`
  width: 66px;
  background-color: #eaf5ff;
`;

const Ol = styled.ol`
  display: grid;
  row-gap: 4px;
  list-style-type: decimal;
  padding: 0 15px;

  & > li {
    ${fontFrame('400', '13px', '1', BLACK)};
  }
`;

const NoticeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  row-gap: 8px;
`;

const OrangeStrong = styled.strong`
  color: ${ORANGE};
`;

const Strong = styled.strong`
  text-decoration: underline;
  font-weight: bold;
`;

/**
 *  타금융사 할부비교 할부조건 테이블 컴포넌트
 *  */
function OtherBankConditionTable(): JSX.Element {
  // device
  const device: string = useDevice();

  // select
  const installmentPrincipal: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankInstallmentPrincipal' })
  );
  // function
  const { updateInstallment } = useCalculatorForOtherBankCondition();
  useEffect(() => {
    installmentPrincipal === 0 &&
      updateInstallment({
        inputName: '',
        inputValue: 0,
      });
  }, []);
  return (
    <Table summary='할부조건 테이블'>
      <thead>
        <tr>
          <Header colSpan={2}>
            <Span fontSize='18px' fontWeight='500'>
              할부비교
            </Span>
          </Header>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Th>
            <Span>구분</Span>
          </Th>
          <td>
            <OtherBankTypeContents />
          </td>
        </tr>
        <tr>
          <Th>
            <Span>기간</Span>
          </Th>
          <td>
            <OtherBankPeriodContents />
          </td>
        </tr>
        <tr>
          <Th rowSpan={3}>
            <Span>원금</Span>
          </Th>
          <td className='not-border-bottom'>
            <OtherBankAdvanceReceivedRateContents />
          </td>
        </tr>
        <tr>
          <td className='not-border-bottom'>
            <OtherBankPaymentTypeContents />
          </td>
        </tr>
        <tr>
          <td>
            <OtherBankInstallmentPrincipalContents />
          </td>
        </tr>
        <tr>
          <td colSpan={2}>
            <NoticeWrapper>
              <Span>* 타 금융사 할부 견적 이용안내</Span>
              <Ol>
                <li>
                  <Span>기간 및 선수율 선택</Span>
                </li>
                <li>
                  <Span>월 납입급 등 확인</Span>
                </li>
                <li>
                  <Span>
                    <OrangeStrong>적용</OrangeStrong> 클릭 후 세부조건 변경
                  </Span>
                </li>
                <li>
                  <Span>견적서 하단에 있는 금융상담신청 버튼 클릭</Span>
                </li>
              </Ol>
              {device === 'pc' && (
                <>
                  <br />
                  <Span>
                    금융상담신청 버튼을 클릭하시면 담당자가 내용 확인 후 진행이 가능하도록 연락을
                    드리겠습니다.
                  </Span>
                  <Span>
                    문의사항은 <Strong>1566-1638</Strong> 로 연락 주시면 자세하게 안내해
                    드리겠습니다.
                  </Span>
                </>
              )}
            </NoticeWrapper>
          </td>
        </tr>
      </tbody>
    </Table>
  );
}

export default React.memo(OtherBankConditionTable);
